import { GridCellParams, GridColDef, GridSortModel } from '@mui/x-data-grid-pro'
import { styleConstants, themeConstants } from '@paywithextend/components'
import moment from 'moment-timezone'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { chevronColumnDef } from 'src/components/DataGrid/cells/ChevronCell'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import DataGrid, {
  DataGridRowHeight,
  handleCellClick,
} from 'src/components/DataGrid/DataGrid'
import NoPeople from 'src/components/NoPeople/NoPeople'
import { PeopleFiltersBody } from 'src/components/PeopleBoxAndLinks/PeopleBoxAndLinks'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { AvatarSizes, OrganizationRoles } from 'src/enums'
import { useFlyoutDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { OrgInviteModel } from 'src/models/people.models'
import Lnk, { connectedPush } from 'src/navigation/Lnk'
import {
  peopleCancelOrgInviteUrl,
  peopleInvitedToOrgDetailUrl,
  peopleInvitedToOrgUrl,
} from 'src/navigation/urls'
import { useInviteToOrganization } from 'src/queries/organizations/organizationInvites.queries'
import { createSetIsCancelOrgInvitePopupOpen } from 'src/redux/actions/app.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { InfiniteScrollProps } from 'src/types'
import { css } from 'styled-components'

interface PeopleInvitedToOrgDataGridProps {
  invites: OrgInviteModel[]
  clientHeight?: number
  infiniteScrollProps?: InfiniteScrollProps<PeopleFiltersBody>
  filtersBody?: PeopleFiltersBody
  sortableProps: {
    setSort: (field: string) => void
    sortBy?: string
    sortDescending?: boolean
  }
}

interface PeopleInvitedToOrgDataGridRow {
  id: string
  invite: OrgInviteModel
  index: number
}

enum sortValues {
  name = 'firstName',
  status = 'status',
  knownSince = 'createdAt',
}

const PeopleInvitedToOrgDataGrid: React.FC<PeopleInvitedToOrgDataGridProps> = (
  props,
) => {
  const translate = useTranslate()
  const dispatch = useDispatch()
  const flyoutDispatch = useFlyoutDispatch()
  const inviteToOrganization = useInviteToOrganization()
  const { analytics } = useAnalyticsContext()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const userOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )

  const [sortModel, setSortModel] = useState<GridSortModel>([])

  // locale
  useEffect(() => {
    if (locale !== undefined) {
      moment.locale(locale)
    }
  }, [locale])

  useEffect(() => {
    if (sortModel?.length > 0) {
      let sortField: sortValues
      switch (sortModel?.[0]?.field) {
        case 'status':
          sortField = sortValues.status
          break
        case 'invitedOn':
          sortField = sortValues.knownSince
          break
        case 'name':
        default:
          sortField = sortValues.name
          break
      }
      props.sortableProps?.setSort?.(sortField)
      analytics?.track(
        'people invited to org list - sort people - table header press',
        {
          screen: '/people/invited',
          interaction: 'table header press',
          intent: 'sort people',
          sortField,
          sortDirection: props.sortableProps?.sortDescending ? 'DESC' : 'ASC',
        },
      )
    }
  }, [sortModel])

  const dispatchReinvite = (invite: OrgInviteModel): void => {
    analytics?.track(
      'people invited to org list - reinvite to org - text link press',
      {
        screen: '/people/invited',
        interaction: 'text link press',
        intent: 'reinvite to org',
      },
    )
    inviteToOrganization.mutate({
      organizationId: userOrganization?.id || '',
      body: {
        emails: [invite.invitee.email],
        role: invite.role,
        bookkeeperAccountVisibilityType:
          invite.role === OrganizationRoles.bookkeeper
            ? invite.accountVisibility?.type
            : undefined,
        bookkeeperAccounts:
          invite.role === OrganizationRoles.bookkeeper
            ? invite.accountVisibility?.ids
            : undefined,
        cardManagerAccounts:
          invite.role === OrganizationRoles.cardManager
            ? invite.accountVisibility?.ids
            : undefined,
      },
      repeat: true,
    })
  }

  const dispatchCancel = (): void => {
    analytics?.track(
      'people invited to org list - cancel invite to org - text link press',
      {
        screen: '/people/invited',
        interaction: 'text link press',
        intent: 'cancel invite to org',
      },
    )
    flyoutDispatch(createSetIsCancelOrgInvitePopupOpen(true))
  }

  const rows: PeopleInvitedToOrgDataGridRow[] = props.invites.map(
    (invite, index) => ({
      id: invite.id,
      invite,
      index,
    }),
  )

  const columns: Array<GridColDef<PeopleInvitedToOrgDataGridRow>> = [
    {
      field: 'name',
      headerName: translate('Name'),
      flex: 2,
      minWidth: 220,
      // sorting temporarily disabled
      sortable: false,
      renderCell: (params): React.ReactElement => {
        const invitee = params.row.invite.invitee
        return (
          <DataGridCell
            icon={
              <UserAvatar
                css={css`
                  display: flex;
                  margin: 2px 0;
                `}
                user={invitee}
                size={AvatarSizes.Small}
              />
            }
            // name for registered invitees, the email for unregistered ones;
            // email always lives in its own span (here or the subtext) so it
            // stays a stable, exact-text target for e2e row selection.
            text={invitee.fullNameOrEmail}
            subtext={invitee.hasFullName ? invitee.email : undefined}
            params={params}
          />
        )
      },
    },
    {
      field: 'status',
      headerName: translate('Status'),
      flex: 1,
      minWidth: 140,
      // sorting temporarily disabled
      sortable: false,
      renderCell: (params): React.ReactElement => (
        <DataGridCell
          text={
            params.row.invite.invitee.verified
              ? translate('Registered')
              : translate('Unregistered')
          }
          params={params}
        />
      ),
    },
    {
      field: 'invitedOn',
      headerName: translate('Invited On'),
      flex: 1,
      minWidth: 130,
      // sorting temporarily disabled
      sortable: false,
      renderCell: (params): React.ReactElement => (
        <DataGridCell
          text={moment(params.row.invite.createdAt).format('L')}
          params={params}
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 170,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (
        params: GridCellParams<PeopleInvitedToOrgDataGridRow>,
      ): React.ReactElement => {
        const invite = params.row.invite
        return (
          <DataGridCellWrapper params={params}>
            <span
              css={css`
                display: flex;
                flex-direction: row;
                gap: 15px;
              `}
            >
              <Lnk
                css={css`
                  color: ${themeConstants.colors.cta};
                  text-decoration: underline;
                `}
                to={peopleInvitedToOrgUrl()}
                onClick={(): void => dispatchReinvite(invite)}
                ariaLabelSource={'reinvite'}
              >
                {translate('Reinvite')}
              </Lnk>
              <Lnk
                css={css`
                  color: ${styleConstants.colors.red};
                  text-decoration: underline;
                `}
                to={peopleCancelOrgInviteUrl(invite.id)}
                onClick={(): void => dispatchCancel()}
                ariaLabelSource={'cancel'}
              >
                {translate('Cancel')}
              </Lnk>
            </span>
          </DataGridCellWrapper>
        )
      },
    },
    chevronColumnDef,
  ]

  return (
    <DataGrid
      height={props.clientHeight}
      rows={rows}
      columns={columns}
      filtersBody={props.filtersBody || {}}
      infiniteScrollProps={props.infiniteScrollProps}
      rowHeight={DataGridRowHeight.Comfy}
      sortModel={sortModel}
      onSortModelChange={(newSortModel): void => setSortModel(newSortModel)}
      disableColumnFilter
      slots={{
        noRowsOverlay: (): React.ReactElement => (
          <div
            css={css`
              display: flex;
              width: 100%;
              height: 100%;
              align-items: center;
              justify-content: center;
            `}
          >
            <NoPeople
              titleText={translate('No one matched your search.')}
              topSubtitleText={translate(
                'Check the keyword you typed for any errors.',
              )}
            />
          </div>
        ),
      }}
      onCellClick={handleCellClick}
      onRowClick={(row): void => {
        const invite = row.row.invite
        dispatch(
          connectedPush({
            pathname: peopleInvitedToOrgDetailUrl(invite.invitee.id),
            search: '',
            hash: '',
            state: {},
          }),
        )
        analytics?.track(
          'people invited to org list - open user detail - list item press',
          {
            screen: '/people/invited',
            interaction: 'list item press',
            intent: 'open user detail',
            viewedUserId: invite.invitee.id,
          },
        )
      }}
    />
  )
}

export default PeopleInvitedToOrgDataGrid
